import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Grid, Box, Typography, TextField, Button, MenuItem, Select, FormControl, InputLabel, CircularProgress } from "@mui/material";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from "recharts";
import DataTable from "react-data-table-component";
import { DollarSign, Download, Filter } from "lucide-react";
import moment from "moment";
import { getRevenueRecords } from "../../../redux/actions/revenueAction";
import { export_revenue_records } from "../../../utils/api-routes";
import RouteHeader from "../../../components/common/RouteHeader";

const COLORS = ["#6366f1", "#3b82f6", "#22c55e", "#f59e0b"];

const SummaryCard = ({ title, amount, color, icon }) => (
    <Box sx={{ background: "#fff", borderRadius: "12px", p: 2.5, boxShadow: "0 1px 6px rgba(0,0,0,0.07)", height: "100%" }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Box>
                <Typography sx={{ fontSize: 13, color: "#64748b", mb: 0.5 }}>{title}</Typography>
                <Typography sx={{ fontSize: 22, fontWeight: 700, color: "#1e293b" }}>₹{Number(amount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</Typography>
            </Box>
            <Box sx={{ width: 44, height: 44, borderRadius: "50%", background: `${color}20`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                {icon || <DollarSign size={22} color={color} />}
            </Box>
        </Box>
    </Box>
);

const RevenueDistribution = () => {
    const dispatch = useDispatch();
    const { revenueRecords, revenueSummary, revenueLoading } = useSelector(state => state.revenueReducer);
    const role = localStorage.getItem("role");

    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [filterBy, setFilterBy] = useState("");
    const [filterId, setFilterId] = useState("");

    useEffect(() => {
        fetchRecords();
    }, [dispatch]);

    const fetchRecords = () => {
        dispatch(getRevenueRecords({ start_date: startDate, end_date: endDate, filter_by: filterBy || undefined, filter_id: filterId || undefined }));
    };

    const handleExport = () => {
        const url = export_revenue_records({ start_date: startDate, end_date: endDate, filter_by: filterBy || undefined, filter_id: filterId || undefined });
        const token = localStorage.getItem("accessToken");
        window.open(`${process.env.REACT_APP_BASE_URL || ""}/${url}&token=${token}`, "_blank");
    };

    const pieData = revenueSummary ? [
        { name: "Super Admin", value: Number(revenueSummary.super_admin_revenue || 0) },
        { name: "Admin", value: Number(revenueSummary.admin_revenue || 0) },
        { name: "Partner", value: Number(revenueSummary.partner_revenue || 0) },
        { name: "Affiliate", value: Number(revenueSummary.affiliate_revenue || 0) },
    ].filter(d => d.value > 0) : [];

    const columns = [
        { name: "Date", selector: row => moment(row.createdAt).format("DD MMM YYYY"), sortable: true, width: "120px" },
        { name: "Customer", selector: row => row.customer_name || "-", sortable: true },
        { name: "Report Type", selector: row => row.report_type || "-", width: "120px" },
        { name: "Total (₹)", selector: row => Number(row.total_amount || 0).toFixed(2), sortable: true, right: true, width: "110px" },
        { name: "Super Admin (₹)", selector: row => Number(row.super_admin_share || 0).toFixed(2), sortable: true, right: true, width: "140px" },
        { name: "Admin (₹)", selector: row => Number(row.admin_share || 0).toFixed(2), sortable: true, right: true, width: "110px" },
        { name: "Partner (₹)", selector: row => Number(row.partner_share || 0).toFixed(2), sortable: true, right: true, width: "120px" },
        { name: "Affiliate (₹)", selector: row => Number(row.affiliate_share || 0).toFixed(2), sortable: true, right: true, width: "120px" },
        { name: "Partner Name", selector: row => row.partner_name || "-", width: "140px" },
        { name: "Admin Name", selector: row => row.admin_name || "-", width: "130px" },
    ];

    const customStyles = {
        headCells: { style: { backgroundColor: "#f8fafc", fontWeight: 600, fontSize: 13, color: "#475569" } },
        cells: { style: { fontSize: 13 } },
    };

    return (
        <Box sx={{ p: 2 }}>
            <RouteHeader title="Revenue Distribution" subtitle="Track revenue splits across all stakeholders" />

            {/* Filters */}
            <Box sx={{ background: "#fff", borderRadius: "12px", p: 2, mb: 2, boxShadow: "0 1px 6px rgba(0,0,0,0.07)", display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center" }}>
                <TextField size="small" type="date" label="Start Date" value={startDate} onChange={e => setStartDate(e.target.value)} InputLabelProps={{ shrink: true }} />
                <TextField size="small" type="date" label="End Date" value={endDate} onChange={e => setEndDate(e.target.value)} InputLabelProps={{ shrink: true }} />
                {role === "Superadmin" && (
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                        <InputLabel>Filter By</InputLabel>
                        <Select value={filterBy} label="Filter By" onChange={e => { setFilterBy(e.target.value); setFilterId(""); }}>
                            <MenuItem value="">All</MenuItem>
                            <MenuItem value="admin">Admin</MenuItem>
                            <MenuItem value="partner">Partner</MenuItem>
                            <MenuItem value="affiliate">Affiliate</MenuItem>
                        </Select>
                    </FormControl>
                )}
                {filterBy && (
                    <TextField size="small" label={`${filterBy.charAt(0).toUpperCase() + filterBy.slice(1)} ID`} value={filterId} onChange={e => setFilterId(e.target.value)} placeholder="Enter ID" />
                )}
                <Button variant="contained" onClick={fetchRecords} disabled={revenueLoading} startIcon={revenueLoading ? <CircularProgress size={16} color="inherit" /> : <Filter size={16} />} sx={{ textTransform: "none", background: "linear-gradient(135deg, #6366f1, #8b5cf6)", "&:hover": { background: "linear-gradient(135deg, #4f46e5, #7c3aed)" } }}>
                    {revenueLoading ? "Loading..." : "Apply"}
                </Button>
                <Button variant="outlined" onClick={handleExport} startIcon={<Download size={16} />} sx={{ textTransform: "none", ml: "auto" }}>
                    Export CSV
                </Button>
            </Box>

            {/* Summary Cards */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6} md={2.4}>
                    <SummaryCard title="Total Revenue" amount={revenueSummary?.total_revenue} color="#6366f1" />
                </Grid>
                <Grid item xs={12} sm={6} md={2.4}>
                    <SummaryCard title="Super Admin" amount={revenueSummary?.super_admin_revenue} color="#6366f1" />
                </Grid>
                <Grid item xs={12} sm={6} md={2.4}>
                    <SummaryCard title="Admin" amount={revenueSummary?.admin_revenue} color="#3b82f6" />
                </Grid>
                <Grid item xs={12} sm={6} md={2.4}>
                    <SummaryCard title="Partner" amount={revenueSummary?.partner_revenue} color="#22c55e" />
                </Grid>
                <Grid item xs={12} sm={6} md={2.4}>
                    <SummaryCard title="Affiliate" amount={revenueSummary?.affiliate_revenue} color="#f59e0b" />
                </Grid>
            </Grid>

            {/* Chart + Table Row */}
            <Grid container spacing={2}>
                {/* Pie Chart */}
                <Grid item xs={12} md={4}>
                    <Box sx={{ background: "#fff", borderRadius: "12px", p: 2, boxShadow: "0 1px 6px rgba(0,0,0,0.07)", height: "100%" }}>
                        <Typography sx={{ fontSize: 15, fontWeight: 600, mb: 1, color: "#1e293b" }}>Revenue Split</Typography>
                        {pieData.length > 0 ? (
                            <ResponsiveContainer width="100%" height={280}>
                                <PieChart>
                                    <Pie data={pieData} cx="50%" cy="50%" innerRadius={55} outerRadius={100} paddingAngle={3} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                                        {pieData.map((entry, index) => (
                                            <Cell key={index} fill={COLORS[index % COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip formatter={(value) => `₹${Number(value).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`} />
                                    <Legend />
                                </PieChart>
                            </ResponsiveContainer>
                        ) : (
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: 280, color: "#94a3b8" }}>
                                <Typography>No data available</Typography>
                            </Box>
                        )}
                    </Box>
                </Grid>

                {/* Data Table */}
                <Grid item xs={12} md={8}>
                    <Box sx={{ background: "#fff", borderRadius: "12px", boxShadow: "0 1px 6px rgba(0,0,0,0.07)", overflow: "hidden" }}>
                        <DataTable
                            columns={columns}
                            data={revenueRecords || []}
                            customStyles={customStyles}
                            pagination
                            paginationPerPage={10}
                            paginationRowsPerPageOptions={[10, 25, 50]}
                            noDataComponent={<Box sx={{ p: 4, color: "#94a3b8" }}>No revenue records found</Box>}
                            dense
                        />
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
};

export default RevenueDistribution;
